/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid, validate } from 'uuid';
import QCXSelectCFOPOperacoesFiscaisAutocomplete from '../../shared-components/final-autocomplete-select-cfop-operacoes-fiscais-field/QCXSelectCFOPOperacoesFiscaisAutocomplete';
import QCXFinalBondManager from '../../shared-components/final-gerenciador-relacionamento/QCXFinalBondManager';
import QCXFinalNumericIntegerField from '../../shared-components/final-numeric-integer-field/QCXFinalNumericIntegerField';
import {
  BondManagerFormProps,
  BondManagerListProps,
} from '../../shared-components/final-gerenciador-relacionamento/QCXFinalBondManagerTypes';
import { selectRegimesTributacao } from '../../features/regime-tributacao/regimeTributacaoSlice';
import TableCellWithTooltip from '../../shared-components/tooltip-cell/TableCellWithTooltip';
import QCXSelectRegimeTributacaoAutocomplete from '../../shared-components/select-regime-tributacao/QCXSelectRegimeTributacaoAutocomplete';
import QCXSelectMotivoDesoneracaoICMSAutocomplete from '../../shared-components/select-motivo-desoneracao-icms/QCXSelectMotivoDesoneracaoICMSAutocomplete';
import QCXSelectICMSDiferidoAutocomplete from '../../shared-components/select-icms-diferido/QCXSelectICMSDiferidoAutocomplete';
import QCXFinalCheckboxField from '../../shared-components/final-checkbox-field/QCXFinalCheckboxField';

type Props = {
  isParentConsult: boolean;
};

type CSTBond = {
  id: string;
  regimeTributacaoIi: {
    id: string;
    description: string;
  };
  regimeTributacaoIpi: {
    id: string;
    description: string;
  };
  regimeTributacaoIbs: {
    id: string;
    description: string;
  };
  regimeTributacaoCbs: {
    id: string;
    description: string;
  };
  regimeTributacaoIcms: {
    id: string;
    description: string;
  };
  regimeTributacaoPisCofins: {
    id: string;
    description: string;
  };
  enquadramentoLegalIpi: number;
  motivoDesoneracaoIcms: string;
  icmsDiferido: string;
  cfop: {
    id: string;
    detalhe: string;
  };
  cst: string;
  cstPis: string;
  cstCofins: string;
  cstIpi: string;
  cstIbs: string;
  cstCbs: string;
};

export function normalizeCSTBond(cstBond: CSTBond) {
  const isLocalId = validate(cstBond?.id);

  const normalizedCSTBond = {
    ...cstBond,
    id: isLocalId ? null : cstBond?.id,
  };

  return normalizedCSTBond;
}

export function unnormalizeCSTBond(data: CSTBond): CSTBond {
  return {
    ...data,
    id: data?.id || uuid(),
  };
}

function CSTBondManager({ isParentConsult }: Props) {
  const { t } = useTranslation();
  const { values } = useFormState();

  const regimesTributacao = useSelector(selectRegimesTributacao);

  const getRegimeTributacaoDescriptionById = (id: string) =>
    regimesTributacao?.find((r: any) => r.id === id)?.description;

  const isIcmsReducao =
    values?.ignorableFields?.regimeTributacaoIcms?.id &&
    getRegimeTributacaoDescriptionById(values.ignorableFields.regimeTributacaoIcms.id)?.toLowerCase() === 'reducao';

  const isAnyRegimeTributacaoFilled = !!(
    values?.ignorableFields?.regimeTributacaoIcms?.id ||
    values?.ignorableFields?.regimeTributacaoIpi?.id ||
    values?.ignorableFields?.regimeTributacaoIi?.id ||
    values?.ignorableFields?.regimeTributacaoPisCofins?.id
  );

  const listProps: BondManagerListProps<CSTBond> = {
    name: 'cstList',
    columns: [
      {
        field: 'regimeTributacaoIi',
        headerName: 'Regime Tributação do II',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoIi?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoIi?.id) || '-'} />
        ),
      },
      {
        field: 'regimeTributacaoIpi',
        headerName: 'Regime Tributação do IPI',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoIpi?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoIpi?.id) || '-'} />
        ),
      },
      {
        field: 'regimeTributacaoIbs',
        headerName: 'Regime Tributação do IBS',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoIbs?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoIbs?.id) || '-'} />
        ),
      },
      {
        field: 'regimeTributacaoCbs',
        headerName: 'Regime Tributação do CBS',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoCbs?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoCbs?.id) || '-'} />
        ),
      },
      {
        field: 'regimeTributacaoPisCofins',
        headerName: 'Regime Tributação do PIS/COFINS',
        headerAlign: 'left',
        align: 'left',
        width: 250,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoPisCofins?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoPisCofins?.id) || '-'} />
        ),
      },
      {
        field: 'regimeTributacaoIcms',
        headerName: 'Regime Tributação do ICMS',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => getRegimeTributacaoDescriptionById(row?.regimeTributacaoIcms?.id) || '-',
        renderCell: ({ row }) => (
          <TableCellWithTooltip value={getRegimeTributacaoDescriptionById(row?.regimeTributacaoIcms?.id) || '-'} />
        ),
      },
      {
        field: 'motivoDesoneracaoIcms',
        headerName: 'Motivo Desoneração do ICMS',
        headerAlign: 'left',
        align: 'left',
        width: 250,
        valueGetter: ({ row }) => {
          console.log('row -> :: ', row);
          return row?.motivoDesoneracaoIcms || '-';
        },
        renderCell: ({ row }) => <TableCellWithTooltip value={row?.motivoDesoneracaoIcms} />,
      },
      {
        field: 'icmsDiferido',
        headerName: 'ICMS Diferido',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.icmsDiferido || '-',
        renderCell: ({ row }) => <TableCellWithTooltip value={row?.icmsDiferido} />,
      },
      {
        field: 'cfop',
        headerName: t('com.muralis.qcx.CFOP.label'),
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cfop?.detalhe || '-',
      },
      {
        field: 'cst',
        headerName: t('com.muralis.qcx.ICMS.CSTICMS'),
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cst || '-',
      },
      {
        field: 'cstPis',
        headerName: t('com.muralis.qcx.CST.CSTPIS'),
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cstPis || '-',
      },
      {
        field: 'cstCofins',
        headerName: t('com.muralis.qcx.CST.CSTCOFINS'),
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cstCofins || '-',
      },
      {
        field: 'cstIpi',
        headerName: t('com.muralis.qcx.CST.CSTIPI'),
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cstIpi || '-',
      },
      {
        field: 'cstIbs',
        headerName: 'CST do Ibs',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cstIbs || '-',
      },
      {
        field: 'cstCbs',
        headerName: 'CST do Cbs',
        headerAlign: 'left',
        align: 'left',
        width: 200,
        valueGetter: ({ row }) => row?.cstCbs || '-',
      },
    ],
  };

  const formProps: BondManagerFormProps = {
    rootName: 'ignorableFields',
    fields: [
      {
        name: 'regimeTributacaoIi.id',
        label: 'Regime Tributação do II',
        required: isAnyRegimeTributacaoFilled,
      },
      {
        name: 'regimeTributacaoIpi.id',
        label: 'Regime Tributação do IPI',
        required: isAnyRegimeTributacaoFilled,
      },
      {
        name: 'regimeTributacaoPisCofins.id',
        label: 'Regime Tributação do PIS/COFINS',
        required: isAnyRegimeTributacaoFilled,
      },
      {
        name: 'regimeTributacaoIcms.id',
        label: 'Regime Tributação do ICMS',
        required: isAnyRegimeTributacaoFilled,
      },
      {
        name: 'enquadramentoLegalIpi',
        label: 'Enquadramento Legal do IPI',
        required: false,
      },
      {
        name: 'motivoDesoneracaoIcms',
        label: 'Motivo Desoneração do ICMS',
        required: false,
      },
      {
        name: 'icmsDiferido',
        label: 'ICMS Diferido',
        required: false,
      },
      {
        name: 'cfop.id',
        label: 'CFOP',
        required: true,
      },
      {
        name: 'cst',
        label: 'CST/ICMS',
        required: false,
      },
      {
        name: 'cstPis',
        label: 'CST PIS',
        required: false,
      },
      {
        name: 'cstCofins',
        label: 'CST COFINS',
        required: false,
      },
      {
        name: 'cstIpi',
        label: 'CST IPI',
        required: false,
      },
      {
        name: 'cstIbs',
        label: 'CST IBS',
        required: false,
      },
      {
        name: 'cstCbs',
        label: 'CST CBS',
        required: false,
      },
    ],
  };

  const handleAddCST = useCallback(async (currentVinculo: any, currentVinculos: any, handleSuccessAddVinculo: any) => {
    const newVinculo = {
      ...currentVinculo,
      id: uuid(),
    };

    const updatedVinculos = [...currentVinculos, newVinculo];

    handleSuccessAddVinculo(updatedVinculos);
  }, []);

  const handleUpdateCST = useCallback(
    async (currentVinculo: any, currentVinculos: any, handleSuccessUpdateVinculo: any) => {
      const updatedVinculo = {
        ...currentVinculo,
      };

      const updatedVinculos =
        currentVinculos?.map((existingVinculo: any) =>
          existingVinculo?.id === updatedVinculo?.id ? updatedVinculo : existingVinculo
        ) || currentVinculos;

      handleSuccessUpdateVinculo(updatedVinculos);
    },
    []
  );

  const handleCSTAlreadyExists = useCallback((currentVinculo: any, currentVinculosList: any) => false, []);

  return (
    <QCXFinalBondManager
      isParentConsult={isParentConsult}
      listProps={listProps}
      formProps={formProps}
      handleAdd={handleAddCST}
      handleUpdate={handleUpdateCST}
      handleAlreadyExists={handleCSTAlreadyExists}
      useNewGrid
    >
      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-ii"
          key="select-field-regime-tributacao-ii"
          name="ignorableFields.regimeTributacaoIi.id"
          label="Regime de Tributação do II"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-ipi"
          key="select-field-regime-tributacao-ipi"
          name="ignorableFields.regimeTributacaoIpi.id"
          label="Regime de Tributação do IPI"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-ibs"
          key="select-field-regime-tributacao-ibs"
          name="ignorableFields.regimeTributacaoIbs.id"
          label="Regime de Tributação do IBS"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-cbs"
          key="select-field-regime-tributacao-cbs"
          name="ignorableFields.regimeTributacaoCbs.id"
          label="Regime de Tributação do CBS"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-pis-cofins"
          key="select-field-regime-tributacao-pis-cofins"
          name="ignorableFields.regimeTributacaoPisCofins.id"
          label="Regime de Tributação do PIS/COFINS"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      <Grid item md={6}>
        <QCXSelectRegimeTributacaoAutocomplete
          id="select-field-regime-tributacao-icms-cofins"
          key="select-field-regime-tributacao-icms-cofins"
          name="ignorableFields.regimeTributacaoIcms.id"
          label="Regime de Tributação do ICMS"
          initialValues={values}
          disabled={isParentConsult}
          configureFilterPredicate={undefined}
        />
      </Grid>

      {isIcmsReducao ? (
        <Grid item xs={6}>
          <QCXFinalCheckboxField
            id="checkbox-field-portaria-cat"
            key="checkbox-field-portaria-cat"
            name="isPortariaCat"
            label="Portaria CAT"
            disabled={isParentConsult}
            required={undefined}
          />
        </Grid>
      ) : null}

      {/*
      <Grid item md={6}>
        <QCXFinalTextField
          id="naturezaOperacao"
          key="naturezaOperacao"
          name="naturezaOperacao"
          label="Natureza da operação"
          initialValues={values}
          disabled={isParentConsult}
        />
      </Grid>
      */}

      <Grid item xs={6} sm={6} md={6}>
        <QCXSelectCFOPOperacoesFiscaisAutocomplete
          id="select-cfop"
          key="select-cfop"
          name="ignorableFields.cfop"
          label={t('com.muralis.qcx.CFOP.label')}
          disabled={isParentConsult}
          initialValues={values}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        md={
          values?.ignorableFields?.regimeTributacaoIcms?.id &&
          getRegimeTributacaoDescriptionById(values.ignorableFields.regimeTributacaoIcms.id)?.toLowerCase() ===
            'reducao'
            ? 4
            : 6
        }
      >
        <QCXFinalNumericIntegerField
          id="text-field-cst-icms"
          key="text-field-cst-icms"
          name="ignorableFields.cst"
          label={t('com.muralis.qcx.ICMS.CSTICMS')}
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={3}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXSelectMotivoDesoneracaoICMSAutocomplete
          id="select-field-motivo-desoneracao-icms"
          key="select-field-motivo-desoneracao-icms"
          name="ignorableFields.motivoDesoneracaoIcms"
          label={t('com.muralis.qcx.ICMS.motivoDesoneracaoICMS')}
          initialValues={values}
          disabled={isParentConsult}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXSelectICMSDiferidoAutocomplete
          id="select-field-icms-diferido"
          key="select-field-icms-diferido"
          name="ignorableFields.icmsDiferido"
          label={t('com.muralis.qcx.ICMS.ICMSDiferido')}
          initialValues={values}
          disabled={isParentConsult}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-cst-pis"
          key="text-field-cst-pis"
          name="ignorableFields.cstPis"
          label={t('com.muralis.qcx.CST.CSTPIS')}
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={2}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-cst-cofins"
          key="text-field-cst-cofins"
          name="ignorableFields.cstCofins"
          label={t('com.muralis.qcx.CST.CSTCOFINS')}
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={2}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-cst-ipi"
          key="text-field-cst-ipi"
          name="ignorableFields.cstIpi"
          label={t('com.muralis.qcx.CST.CSTIPI')}
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={2}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-enquadramento-legal-ipi"
          key="text-field-enquadramento-legal-ipi"
          name="ignorableFields.enquadramentoLegalIpi"
          label="Enquadramento legal do IPI"
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={3}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-cst-ibs"
          key="text-field-cst-ibs"
          name="ignorableFields.cstIbs"
          label="CST do Ibs"
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={2}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <QCXFinalNumericIntegerField
          id="text-field-cst-cbs"
          key="text-field-cst-cbs"
          name="ignorableFields.cstCbs"
          label="CST do Cbs"
          disabled={isParentConsult}
          allowNegative={false}
          maxLength={2}
        />
      </Grid>
    </QCXFinalBondManager>
  );
}

export default CSTBondManager;
